const LEGACY_SESSION_TOKEN_KEY = 'session_token';
const LEGACY_USER_SNAPSHOT_KEY = 'session_user_snapshot';
const GUEST_IDENTITY_KEY = 'geovera_guest_identity';

interface SessionUser extends Record<string, unknown> {
  id: string;
  email: string;
  role?: string;
}

export type SessionValidationResult =
  | { valid: true; user: SessionUser }
  | { valid: false; error: string; network?: boolean; unauthorized?: boolean };

function removeLegacySessionState(): void {
  try {
    localStorage.removeItem(LEGACY_SESSION_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_SNAPSHOT_KEY);
  } catch (error) {
    console.error('Failed to clear legacy session state:', error);
  }
}

/**
 * Legacy compatibility shim. Browser authentication is cookie-only; tokens are never persisted.
 */
export const storeSessionToken = (_token: string): void => {
  removeLegacySessionState();
};

/**
 * Legacy compatibility shim. HttpOnly authentication cookies are intentionally unavailable to JS.
 */
export const getSessionToken = (): null => {
  removeLegacySessionState();
  return null;
};

/**
 * Return a stable, non-secret identifier for correlating a guest workspace before sign-in.
 * This value is not an authentication credential.
 */
export const ensureGuestToken = (): string => {
  try {
    const existing = localStorage.getItem(GUEST_IDENTITY_KEY);
    if (existing) return existing;

    const randomId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Math.random().toString(36).slice(2)}_${Date.now()}`;
    const guestIdentity = `guest_${randomId}`;
    localStorage.setItem(GUEST_IDENTITY_KEY, guestIdentity);
    return guestIdentity;
  } catch (error) {
    console.error('Failed to access guest identity:', error);
    return `guest_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  }
};

/** Clear obsolete browser auth state. The non-secret guest identity is intentionally retained. */
export const clearSessionToken = (): void => {
  removeLegacySessionState();
};

const getCsrfToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
  if (!csrfMatch) return null;
  try {
    return decodeURIComponent(csrfMatch[2]);
  } catch {
    return csrfMatch[2];
  }
};

function apiEndpoint(apiUrl: string, path: string): string {
  return `${apiUrl.replace(/\/$/, '')}${path}`;
}

async function fetchCsrfToken(apiUrl: string): Promise<string | null> {
  const existing = getCsrfToken();
  if (existing) return existing;

  try {
    const response = await fetch(apiEndpoint(apiUrl, '/api/csrf-token'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-GeoVera-Client': 'webgis' },
    });
    const data = (await response.json().catch(() => ({}))) as { csrfToken?: string };
    return data.csrfToken || getCsrfToken();
  } catch {
    return getCsrfToken();
  }
}

/**
 * Validate the current HttpOnly-cookie session.
 * The token argument is retained only for source compatibility and is never read or transmitted.
 */
export const validateSessionToken = async (
  _token: string,
  apiUrl: string
): Promise<SessionValidationResult> => {
  removeLegacySessionState();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12_000);

  try {
    const response = await fetch(apiEndpoint(apiUrl, '/api/auth/session'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-GeoVera-Client': 'webgis' },
      signal: controller.signal,
    });

    const data = (await response.json().catch(() => ({}))) as {
      success?: boolean;
      user?: SessionUser;
      message?: string;
    };

    if (response.ok && data.success && data.user) {
      return { valid: true, user: data.user };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        valid: false,
        unauthorized: true,
        error: data.message || 'Session expired or invalid',
      };
    }

    return {
      valid: false,
      network: response.status >= 500 || response.status === 429,
      error: data.message || `Unexpected response (${response.status})`,
    };
  } catch (error) {
    console.error('Session validation error:', error);
    const message =
      error instanceof Error
        ? error.name === 'AbortError'
          ? 'Session check timed out'
          : error.message
        : 'Network error';
    return { valid: false, network: true, error: message };
  } finally {
    clearTimeout(timer);
  }
};

/** Restore the authenticated user from the server-owned cookie session. */
export const restoreSession = async (apiUrl: string): Promise<{ user: SessionUser } | null> => {
  const result = await validateSessionToken('', apiUrl);
  return result.valid ? { user: result.user } : null;
};

/** Terminate the server-owned cookie session and clear obsolete local auth state. */
export const logoutUser = async (apiUrl: string): Promise<{ success: boolean; error?: string }> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10_000);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-GeoVera-Client': 'webgis',
    };
    const csrfToken = await fetchCsrfToken(apiUrl);
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

    const response = await fetch(apiEndpoint(apiUrl, '/api/auth/logout'), {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({}),
      signal: controller.signal,
    });

    if (response.ok || response.status === 401) return { success: true };

    const data = (await response.json().catch(() => ({}))) as { message?: string };
    return { success: false, error: data.message || 'Logout failed' };
  } catch (error) {
    console.error('Logout error:', error);
    return {
      success: true,
      error: error instanceof Error ? error.message : 'Network error during logout',
    };
  } finally {
    clearTimeout(timer);
    clearSessionToken();
  }
};
